/**
 * AdminCompanySettings - Company Information for Checks & Documents
 * 
 * Features:
 * - Company name, address, phone, email
 * - Used on printed checks and official documents
 * - Logo upload (future)
 */
import React, { useState, useEffect } from 'react';
import {
  Building2, Save, Loader2, RefreshCw, Phone, Mail, MapPin
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

const AdminCompanySettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    company_name: '',
    company_address_line1: '',
    company_address_line2: '',
    company_city: '',
    company_state: '',
    company_zip: '',
    company_phone: '',
    company_email: '',
    company_website: '',
    tax_id: '', // EIN for checks
  });

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/settings/company`);
      if (res.ok) {
        const data = await res.json();
        setSettings(prev => ({ ...prev, ...data }));
      }
    } catch (err) {
      console.error('Failed to load company settings', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/settings/company`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(settings)
      });
      
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to save');
      }
      
      toast.success('Company settings saved');
    } catch (err) {
      toast.error(err.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const updateField = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  // Format full address for preview
  const fullAddress = [
    settings.company_address_line1,
    settings.company_address_line2,
    [settings.company_city, settings.company_state, settings.company_zip].filter(Boolean).join(', ')
  ].filter(Boolean).join('\n');

  return (
    <div className="space-y-6" data-testid="admin-company-settings">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-gray-900">Company Information</h2>
          <p className="text-gray-500 text-sm mt-1">
            This information appears on printed checks and official documents
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={fetchSettings}
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button onClick={handleSave} disabled={saving} data-testid="save-company-btn">
            {saving ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Save Changes
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center items-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-gold-500" />
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Form */}
          <div className="lg:col-span-2 space-y-6">
            {/* Basic Info */}
            <div className="bg-white rounded-xl border p-6">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Building2 className="w-5 h-5 text-gray-400" />
                Basic Information
              </h3>
              
              <div className="space-y-4">
                <div>
                  <Label htmlFor="company_name">Company Name *</Label>
                  <Input
                    id="company_name"
                    value={settings.company_name}
                    onChange={(e) => updateField('company_name', e.target.value)}
                    placeholder="Your Company Name"
                    className="mt-1"
                    data-testid="company-name-input"
                  />
                </div>
                
                <div>
                  <Label htmlFor="tax_id">Tax ID / EIN</Label>
                  <Input
                    id="tax_id"
                    value={settings.tax_id}
                    onChange={(e) => updateField('tax_id', e.target.value)}
                    placeholder="XX-XXXXXXX"
                    className="mt-1 font-mono"
                    data-testid="tax-id-input"
                  />
                  <p className="text-xs text-gray-500 mt-1">For official documents</p>
                </div>
              </div>
            </div>

            {/* Address */}
            <div className="bg-white rounded-xl border p-6">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <MapPin className="w-5 h-5 text-gray-400" />
                Address
              </h3>
              
              <div className="space-y-4">
                <div>
                  <Label htmlFor="company_address_line1">Address Line 1</Label>
                  <Input
                    id="company_address_line1"
                    value={settings.company_address_line1}
                    onChange={(e) => updateField('company_address_line1', e.target.value)}
                    placeholder="123 Main Street"
                    className="mt-1"
                    data-testid="address-line1-input"
                  />
                </div>
                
                <div>
                  <Label htmlFor="company_address_line2">Address Line 2</Label>
                  <Input
                    id="company_address_line2"
                    value={settings.company_address_line2}
                    onChange={(e) => updateField('company_address_line2', e.target.value)}
                    placeholder="Suite 100"
                    className="mt-1"
                    data-testid="address-line2-input"
                  />
                </div>
                
                <div className="grid grid-cols-6 gap-4">
                  <div className="col-span-3">
                    <Label htmlFor="company_city">City</Label>
                    <Input
                      id="company_city"
                      value={settings.company_city}
                      onChange={(e) => updateField('company_city', e.target.value)}
                      placeholder="New York"
                      className="mt-1"
                      data-testid="city-input"
                    />
                  </div>
                  <div className="col-span-1">
                    <Label htmlFor="company_state">State</Label>
                    <Input
                      id="company_state"
                      value={settings.company_state}
                      onChange={(e) => updateField('company_state', e.target.value.toUpperCase().slice(0, 2))}
                      placeholder="NY"
                      maxLength={2}
                      className="mt-1"
                      data-testid="state-input"
                    />
                  </div>
                  <div className="col-span-2">
                    <Label htmlFor="company_zip">ZIP Code</Label>
                    <Input
                      id="company_zip"
                      value={settings.company_zip}
                      onChange={(e) => updateField('company_zip', e.target.value)}
                      placeholder="10001"
                      className="mt-1"
                      data-testid="zip-input"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Contact */}
            <div className="bg-white rounded-xl border p-6">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Phone className="w-5 h-5 text-gray-400" />
                Contact Information
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="company_phone">Phone</Label>
                  <Input
                    id="company_phone"
                    value={settings.company_phone}
                    onChange={(e) => updateField('company_phone', e.target.value)}
                    placeholder="(555) 123-4567"
                    className="mt-1"
                    data-testid="phone-input"
                  />
                </div>
                
                <div>
                  <Label htmlFor="company_email">Email</Label>
                  <Input
                    id="company_email"
                    type="email"
                    value={settings.company_email}
                    onChange={(e) => updateField('company_email', e.target.value)}
                    placeholder="info@company.com"
                    className="mt-1"
                    data-testid="email-input"
                  />
                </div>
                
                <div className="md:col-span-2">
                  <Label htmlFor="company_website">Website</Label>
                  <Input
                    id="company_website"
                    value={settings.company_website}
                    onChange={(e) => updateField('company_website', e.target.value)}
                    placeholder="https://www.company.com"
                    className="mt-1"
                    data-testid="website-input"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Preview */}
          <div className="space-y-6">
            <div className="bg-white rounded-xl border p-6 sticky top-4">
              <h3 className="text-lg font-semibold mb-4">Check Preview</h3>
              <p className="text-sm text-gray-500 mb-4">
                How your company info will appear on checks
              </p>
              
              {/* Mini check preview */}
              <div className="border-2 border-dashed border-gray-200 rounded-lg p-4 bg-gray-50">
                <div className="text-center space-y-1">
                  <p className="font-bold text-lg">
                    {settings.company_name || 'Your Company Name'}
                  </p>
                  {settings.company_address_line1 && (
                    <p className="text-sm text-gray-600">{settings.company_address_line1}</p>
                  )}
                  {settings.company_address_line2 && (
                    <p className="text-sm text-gray-600">{settings.company_address_line2}</p>
                  )}
                  {(settings.company_city || settings.company_state || settings.company_zip) && (
                    <p className="text-sm text-gray-600">
                      {[settings.company_city, settings.company_state, settings.company_zip].filter(Boolean).join(', ')}
                    </p>
                  )}
                  {settings.company_phone && (
                    <p className="text-xs text-gray-500 mt-2">{settings.company_phone}</p>
                  )}
                </div>
              </div>
              
              <p className="text-xs text-gray-400 mt-4 text-center">
                This will appear in the top-left corner of printed checks
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCompanySettings;
